import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, CalendarDays, Wallet, Settings, Briefcase,
  ScrollText, LogOut, Menu, X, ReceiptIndianRupee,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/payroll", label: "Payroll Runs", icon: Wallet, id: "payroll" },
  { to: "/admin/employees", label: "Employees", icon: Users, id: "employees" },
  { to: "/admin/designations", label: "Designations", icon: Briefcase, id: "designations" },
  { to: "/admin/holidays", label: "Holidays", icon: CalendarDays, id: "holidays" },
  { to: "/admin/settings", label: "Company Settings", icon: Settings, id: "settings" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, id: "audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 flex items-center gap-2.5 border-b border-border">
        <div className="w-8 h-8 rounded-md bg-[#FF4500] flex items-center justify-center">
          <ReceiptIndianRupee className="w-5 h-5 text-white" />
        </div>
        <div className="leading-tight">
          <div className="font-display font-semibold text-[15px]">Payslip Portal</div>
          <div className="text-[11px] text-muted-foreground">Admin Console</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.id}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-foreground text-background font-medium"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              )
            }
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-border">
        <div className="text-[13px] font-medium truncate">{user?.name}</div>
        <div className="text-[11px] text-muted-foreground truncate mb-3">{user?.email}</div>
        <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={doLogout} data-testid="logout-button">
          <LogOut className="w-4 h-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card fixed inset-y-0">
        <SideContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-card border-b border-border flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[#FF4500] flex items-center justify-center">
            <ReceiptIndianRupee className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-semibold text-sm">Payslip Portal</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
          <Menu className="w-5 h-5" />
        </Button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative w-64 bg-card h-full shadow-xl">
            <button className="absolute top-4 right-4" onClick={() => setOpen(false)} data-testid="mobile-menu-close">
              <X className="w-5 h-5" />
            </button>
            <SideContent />
          </div>
        </div>
      )}

      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0 min-w-0">
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
